(ns common.schema.entities.counterparty-diligence.templates.demo.legal-entity
  (:require
   [common.schema.entities.counterparty-diligence.templates.demo.board-member :as board-member]
   [common.schema.entities.counterparty-diligence.templates.demo.ubo :as ubo]
   [common.schema.entities.counterparty-diligence.templates.questions.board-member :as board-member-q]
   [common.schema.entities.counterparty-diligence.templates.questions.legal-entity :as legal-entity-q]
   [common.schema.entities.counterparty-diligence.templates.questions.person :as person-q]
   [common.schema.entities.counterparty-diligence.templates.questions.common :as common-q]
   [common.schema.entities.counterparty-diligence.templates.demo.ceo :as ceo]
   [common.schema.entities.counterparty-diligence.templates.demo.pep :as pep]
   [common.schema.entities.counterparty-diligence.templates.validation :as validation]
   [clojure.string :as string]
   [clojure.set :as set]))

(def yes-no-options
  [{:value :yes
    :label "Yes"}
   {:value :no
    :label "No"}])

(def template-id :counterparty-diligence-template/demo-legal-entity)

(defn find-key-persons
  [{:keys [data]}]
  (let [ceo (some->> data
                     :ceo
                     (mapv (fn [person]
                             (-> person
                                 (set/rename-keys
                                  {:person/first-name    :first-name
                                   :person/last-name     :last-name
                                   :person/date-of-birth :date-of-birth
                                   :person/nationality   :country})
                                 (assoc :roles #{:ceo})))))
        board-members (some->> data
                               :board-members
                               (mapv (fn [person]
                                       (-> person
                                           (set/rename-keys
                                            {:person/first-name    :first-name
                                             :person/last-name     :last-name
                                             :person/date-of-birth :date-of-birth
                                             :person/nationality   :country})
                                           (assoc :roles #{(:board-member/role person)})))))

        ubos (some->> data
                      :ultimate-beneficial-owners
                      (mapv (fn [person]
                              (-> person
                                  (set/rename-keys
                                   {:person/first-name    :first-name
                                    :person/last-name     :last-name
                                    :person/date-of-birth :date-of-birth
                                    :person/nationality   :country})
                                  (assoc :roles #{:ultimate-beneficial-owner}
                                         :is-pep? (-> person
                                                      :person-is-politically-exposed
                                                      (= :yes)))))))

        peps (some->> data
                      :politically-exposed-persons
                      (mapv (fn [person]
                              (-> person
                                  (set/rename-keys {:pep/first-name :first-name
                                                    :pep/last-name  :last-name})
                                  (assoc :roles #{:politically-exposed-person}
                                         :is-pep? true)))))

        persons (->> (concat ceo
                             board-members
                             ubos
                             peps)
                     (mapv (fn [{:keys [first-name last-name]
                                 :as person}]
                             (assoc person
                                    :first-name (-> first-name
                                                    str
                                                    string/trim)
                                    :last-name (-> last-name
                                                   str
                                                   string/trim))))
                     (filterv (fn [{:keys [first-name last-name]}]
                                (and (seq first-name)
                                     (seq last-name)))))]
    persons))

(def schema
  {:template/id template-id
   :template/template-name "Legal Entity"

   :template/form-title    {:en "Transaction Counterparty KYC Check"}

   :template/form-preface
   {:en (str "<p>In accordance with Anti-Money Laundering (AML) and Counter Terrorist Financing (CTF) regulations, "
             "We are required to collect specific Know Your Customer (KYC) information for all counterparties involved in a transaction. "
             "As part of legal due diligence, a nominated counsel is requested to gather and review the following documentation on the seller, "
             "purchaser and target company and report any suspicious remarks to us.</p>"
             "<br/>"
             "<p>Please fill in the information and attach all reviewed materials to this online form.</p>")}

   :template/find-key-persons find-key-persons

   :template/schema
   (merge
    (->> [legal-entity-q/company-name
          legal-entity-q/business-id
          legal-entity-q/jurisdiction
          legal-entity-q/trade-register-extract-or-eq
          legal-entity-q/articles-of-association-or-equivalent-or-eq]
         (mapv (fn [{:keys [field] :as config}]
                 [field config]))
         (into {}))

    {(:field legal-entity-q/company-legal-form)
     (merge
      legal-entity-q/company-legal-form
      {:linked-fields [(:field legal-entity-q/company-legal-form-other)]})}

    {(:field legal-entity-q/company-legal-form-other)
     (merge
      legal-entity-q/company-legal-form-other
      {:validation-fn (fn [{:keys [value data draft?]}]
                        (let [legal-form-other? (->> legal-entity-q/company-legal-form
                                                     :field
                                                     (get data)
                                                     (= :legal-form/other))
                              mandatoriness (if (or draft?
                                                    (not legal-form-other?))
                                              :mandatory/optional
                                              :mandatory/always)]
                          (validation/validate-text-field
                           {:field-config {:mandatoriness mandatoriness}}
                           value)))
       :visibility-fn (fn [{:keys [data]}]
                        (->> legal-entity-q/company-legal-form
                             :field
                             (get data)
                             (= :legal-form/other)))})}

    {:ownership-structure-chart-showing-beneficial-owners
     {:label     {:en "Ownership structure chart showing beneficial owners (up to individuals)"}
      :data-type :data.type/file}

     :shareholder-register
     {:label     {:en "Shareholder register (including share and voting rights)"}
      :data-type :data.type/file}

     :latest-financial-accounts
     {:label     {:en "Latest financial accounts"}
      :data-type :data.type/file}

     :information-on-the-source-of-funds
     {:label     {:en "Source of funds and wealth"}
      :data-type :data.type/text-multiline}

     :ceo
     {:label          {:en "Chief Executive Officer"}
      :entity-label   {:en "Chief Executive Officer"}
      :data-type      :data.type/vector
      :item-data-type :data.type/map
      :editor-type    :editor.type/entity-list
      :item-template  ceo/schema
      :min-items      1
      :table-columns  [person-q/first-name
                       person-q/last-name
                       person-q/date-of-birth
                       person-q/nationality]
      :item-display-string-fields [person-q/first-name
                                   person-q/last-name]}

     :board-members
     {:label         {:en "Board members"}
      :entity-label  {:en "Board member"}
      :entity-label-plural {:en "Board members"}
      :data-type     :data.type/vector
      :editor-type   :editor.type/entity-list
      :item-data-type :data.type/map
      :min-items     2
      :item-template board-member/schema
      :table-columns [person-q/first-name
                      person-q/last-name
                      board-member-q/role
                      person-q/date-of-birth
                      person-q/nationality]
      :item-display-string-fields [person-q/first-name
                                   person-q/last-name]}

     :ultimate-beneficial-owners
     {:label         {:en "Ultimate Beneficial Owners"}
      :entity-label  {:en "Ultimate Beneficial Owner"}
      :entity-label-plural {:en "Ultimate Beneficial Owners"}
      :data-type     :data.type/vector
      :editor-type   :editor.type/entity-list
      :item-data-type :data.type/map
      :min-items      1
      :item-template  ubo/schema
      :table-columns [person-q/first-name
                      person-q/last-name
                      person-q/date-of-birth
                      person-q/nationality]
      :item-display-string-fields [person-q/first-name
                                   person-q/last-name]}

     :company-has-politically-exposed-persons
     {:label         {:en "Does the company have politically exposed person(s)?"}
      :data-type     :data.type/keyword
      :options       yes-no-options
      :linked-fields [:politically-exposed-persons]}

     :politically-exposed-persons
     {:label            {:en "Politically exposed persons"}
      :entity-label     {:en "Politically exposed person"}
      :visibility-fn    (fn [{:keys [data]}]
                          (-> data
                              (get :company-has-politically-exposed-persons)
                              (= :yes)))
      :data-type        :data.type/vector
      :item-data-type   :data.type/map
      :item-template    pep/schema
      :editor-type      :editor.type/entity-list
      :table-columns    (->> [:pep/first-name
                              :pep/last-name]
                             (mapv (fn [field]
                                     (-> pep/schema
                                         (get-in [:template/schema field])
                                         (assoc :field field)))))
      :item-display-string-fields (->> [:pep/first-name
                                        :pep/last-name]
                                       (mapv (fn [field]
                                               (-> pep/schema
                                                   (get-in [:template/schema field])
                                                   (assoc :field field)))))}}

    {(:field common-q/signatories) common-q/signatories})

   :template/sections
   {:basic-information
    {:ord   1
     :title {:en "Basic information"}
     :fields (->> [legal-entity-q/company-name
                   legal-entity-q/business-id
                   legal-entity-q/jurisdiction
                   legal-entity-q/company-legal-form
                   legal-entity-q/company-legal-form-other
                   legal-entity-q/trade-register-extract-or-eq
                   legal-entity-q/articles-of-association-or-equivalent-or-eq]
                  (mapv :field))}

    :organization-and-ownership-details
    {:ord   2
     :title {:en "Organization and ownership details"}
     :fields [:ownership-structure-chart-showing-beneficial-owners
              :shareholder-register]}

    :financial-information
    {:ord   3
     :title {:en "Financial information"}
     :fields [:latest-financial-accounts
              :information-on-the-source-of-funds]}

    :key-persons
    {:ord   4
     :title {:en "Key persons"}
     :fields [:ceo
              :board-members
              :ultimate-beneficial-owners]}

    :politically-exposed-persons
    {:ord   5
     :title {:en "Politically exposed persons"}
     :fields [:company-has-politically-exposed-persons
              :politically-exposed-persons]}

    :signatories
    {:ord                6
     :title              {:en "Signatories"}
     :info-text          {:en "After submission, a pdf document will be sent to these persons for e-sign"}
     :signatory-section? true
     :fields             [:signatories]}}})
